// Lib
import { includes, union, difference } from 'lodash/fp';

/**
 * Expects a data structure as follows:
 * {
 *    ID: [ CHILD_ID, CHILD_ID ],
 *    ...
 * }
 */

export const getChildIds = ({ graph, parentId }) => graph[parentId] || [];

/**
 * Gets all the child IDs for multiple parent IDs.
 */
export const getAllChildIds = ({ graph, parentIds }) => {
    if (!parentIds) return [];
    return parentIds.reduce((acc, id) => acc.concat(getChildIds({ graph, parentId: id })), []);
};

/**
 * Returns a depth first traversal of a graph, including the depth of the vertex.
 * Result is in the form { id, depth }.
 */
export const depthFirstSearch = ({ graph, startingId, maxDepth = 0, visited = {}, depth = 0, result = [] }) => {
    const childIds = getChildIds({ graph, parentId: startingId });
    const alreadyVisited = visited[startingId];

    if (!childIds || alreadyVisited) return result;

    result.push({ id: startingId, depth });
    visited[startingId] = true;

    if (maxDepth && depth === maxDepth) return result;

    return childIds.reduce(
        (res, childId) =>
            depthFirstSearch({
                graph,
                startingId: childId,
                maxDepth,
                visited,
                depth: depth + 1,
                result,
            }),
        result,
    );
};

/**
 * The same as depth first search, but it doesn't track depth and should be more efficient
 * because it doesn't perform object allocations.
 * This is useful for large graph traversal that happens regularly.
 */
export const simpleDepthFirstSearch = (graph, startingId, visited = {}, result = []) => {
    if (!startingId) return result;

    const childIds = graph[startingId] || [];
    const alreadyVisited = visited[startingId];

    if (!childIds || alreadyVisited) return result;

    result.push(startingId);
    visited[startingId] = true;

    return childIds.reduce((res, childId) => simpleDepthFirstSearch(graph, childId, visited, result), result);
};

export const getFromDepthFirstSearch = (dfsResult, id) => {
    if (!dfsResult || !id) return null;
    return dfsResult.find((entry) => entry.id === id);
};

export const getIndexFromDepthFirstSearch = (dfsResult, id) => {
    if (!dfsResult || !id) return -1;
    return dfsResult.findIndex((entry) => entry.id === id);
};

export const getAllIdsStartingFrom = ({ graph, startingId }) =>
    depthFirstSearch({ graph, startingId }).map((entry) => entry.id);

export const getDescendantIds = ({ graph, parentId }) =>
    getAllIdsStartingFrom({ graph, startingId: parentId }).slice(1);

export const isDescendantId = ({ graph, parentId, targetId }) => {
    const descendants = getDescendantIds({ graph, parentId });
    return includes(targetId, descendants);
};

/**
 * Returns only the portion of the graph that can be reached from the starting ID.
 */
export const getSubGraph = ({ graph, startingId, subGraph = {} }) => {
    // Prevent infinite loops
    if (subGraph[startingId]) return subGraph;

    const childIds = getChildIds({ graph, parentId: startingId });

    subGraph[startingId] = childIds;

    childIds.forEach((childId) => getSubGraph({ graph, startingId: childId, subGraph }));

    return subGraph;
};

/**
 * Finds all the groups for a disconnected graph.
 */
export const getConnectedGroups = ({ graph }) => {
    let unvisitedNodes = Object.keys(graph);
    let visitedNodes = [];
    const groups = [];

    while (unvisitedNodes.length > 0) {
        const currentNode = unvisitedNodes[0];
        const treeIds = getAllIdsStartingFrom({ graph, startingId: currentNode });

        groups.push(treeIds);

        visitedNodes = union(visitedNodes, treeIds);
        unvisitedNodes = difference(unvisitedNodes, visitedNodes);
    }

    return groups;
};
